import React from "react";
import { Check } from "lucide-react";

const Pricing = () => {
  const plans = [
    {
      name: "Starter",
      price: "₹4,999",
      desc: "Perfect for small businesses and startups.",
      features: ["1 Website", "Basic SEO", "Email Support"],
    },
    {
      name: "Professional",
      price: "₹14,999",
      desc: "Ideal for growing companies with more needs.",
      features: [
        "Up to 5 Pages",
        "Advanced SEO",
        "Priority Support",
        "Basic Analytics",
      ],
      highlighted: true,
      badge: "Most Popular",
    },
    {
      name: "Enterprise",
      price: "₹29,999",
      desc: "For established brands & large businesses.",
      features: [
        "Unlimited Pages",
        "Full SEO Package",
        "Dedicated Manager",
        "Custom Features",
      ],
    },
  ];

  return (
    <section
      id="pricing"
      className="bg-gradient-to-b from-gray-50 to-white py-28"
      aria-label="Pricing Plans"
    >
      <div className="container mx-auto px-6 max-w-7xl text-center">
        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
          Our Pricing Plans
        </h2>
        <p className="mt-4 max-w-xl mx-auto text-gray-600 text-lg md:text-xl">
          Choose a plan that fits your business goals. Flexible, transparent, and
          designed for growth.
        </p>

        <div className="mt-20 grid gap-12 md:grid-cols-3">
          {plans.map((plan, index) => (
            <article
              key={index}
              className={`relative rounded-3xl p-10 shadow-md transition-shadow duration-300 border 
                ${plan.highlighted
                  ? "bg-white border-indigo-600 ring-4 ring-indigo-500 shadow-2xl z-10 scale-105"
                  : "bg-white border-gray-200 hover:shadow-lg hover:scale-105"
                }`}
              role="region"
              aria-labelledby={`plan-${index}-title`}
            >
              {plan.highlighted && (
                <span
                  className="absolute top-6 right-6 inline-block bg-indigo-600 text-white text-sm font-semibold py-1 px-3 rounded-full tracking-wide shadow-lg"
                  aria-label="Most popular plan badge"
                >
                  {plan.badge}
                </span>
              )}

              <h3
                id={`plan-${index}-title`}
                className="text-2xl font-semibold text-gray-900"
              >
                {plan.name}
              </h3>
              <p className="mt-3 text-gray-500">{plan.desc}</p>

              <p className="mt-6 text-4xl font-extrabold text-indigo-600 tracking-tight">
                {plan.price}
              </p>

              <ul className="mt-8 space-y-5 text-gray-700 text-lg text-left border-t border-gray-200 pt-6">
                {plan.features.map((feature, i) => (
                  <li
                    key={i}
                    className="flex items-center space-x-3"
                    tabIndex={0}
                    aria-label={`Feature: ${feature}`}
                  >
                    <Check
                      size={22}
                      className="text-green-500 flex-shrink-0"
                      aria-hidden="true"
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className={`mt-10 w-full py-4 rounded-xl font-semibold text-lg transition 
                  focus:outline-none focus:ring-4 focus:ring-indigo-400 focus:ring-offset-2
                  ${plan.highlighted
                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                  }`}
                aria-label={`Choose the ${plan.name} plan`}
              >
                Choose {plan.name}
              </button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
